import React, { useState, useEffect, useRef, MutableRefObject } from "react";
import maplibregl, { GeoJSONSource, Map } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useRouter } from "next/router";
import { mapDB } from "../../../store/mapStore";
import { globalDB } from "../../../store/globalStore";
import axios from "axios";
import { MapSidebar } from "../sidebar/MapSidebar";
import { Box, Grid } from "@mui/material";
import { useLiveQuery } from "dexie-react-hooks";
import { AddressPoint } from "../../../types/data/dataTypes";
import { useWindowDimensions } from "../../../utils/WindowDimensions";
import {
  BOUNDS,
  CENTER,
  MAP_LAYERS,
  SOURCE_PB,
  SOURCE_PB_SWAPPABLE,
  SOURCE_SCHOOL_POINTS,
  SOURCE_SCHOOL_BOUNDARIES,
} from "./constants";
import {
  ALL_BOUNDARIES_LAYER,
  PLANNING_BLOCK_LAYER,
  PLANNING_BLOCK_SWAPPABLE,
  SCHOOL_POINTS,
  SEARCHED_ADD_LAYER,
  SCHOOL_BOUNDARIES,
  PLANNING_BLOCK_CHANGE_LAYER,
  DUAL_RIGHTS_LAYER,
} from "./layers";
import {
  useBlocks,
  useBlocksBySchoolData,
  useSchoolData,
} from "../../../api/seedData";
import {
  SchoolID,
  SchoolType,
} from "../../../editing_planning/data_requirements";
import { useCreateBoundaryForAllSchools } from "./shapeManipulation";
import { useEnableEditMode } from "./modes/configureEditing";
import { enableViewMode } from "./modes/configureViewing";
import { useMapContextStore } from "../../../context/MapContext";
import { useMapMode, useUpdateMapMode } from "../../../utils/router";
import { getSwaps } from "../../../editing_planning/func_requirements";
import { getIdFromSchoolPlusLevel } from "../../../utils/editing/helpers";
import { setSwapHighlighting } from "./eventHandlers";
import { useEditingContextStoreRef } from "../../../context/EditingContext";
import { Selectors } from "./config/Selectors";
import { enableAssignmentMode } from "./modes/configureAssignments";
import {
  colorBlocksByLevelAndScenario,
  shadeDualRightsChanges,
  filterSchoolsByLevel,
  hatchOnBoundaryChanges,
  scenarioBoundaryOpacity,
} from "./mapStyling/StyleHandlers";
import { MultiSelectModal } from "../../landing/MultiSelectModal";
import { MobileControls } from "./MobileControls";
import {
  colorBlocksByHovered,
  increaseOpacityWhenSelected,
  showBoundariesByHovered,
  showSchoolsByLevelEditor,
} from "./mapStyling/editSyleHandlers";
import {
  removeHighlightOnAssignmentBlocks,
  highlightAssignmentBlocks,
  filterBySchool,
  showHatchOnChangedBlocksAssignments,
  showDualRightsOnChangedBlocksAssignments,
} from "./mapStyling/assignmentStyleHandlers";
import {
  highlightSelectedSchool,
  filterBlocksByLevel,
  resetBlockOpacities,
} from "./mapStyling/viewStyleHandlers";
import { NotificationSnackBar } from "../NotificationSnackBar";
import { NoEditModal } from "../../landing/NoEditModal";
import { MapHeader } from "../../global/MapHeader";
import { MapLegend } from "../MapLegend";

export const MapPage: React.FC = () => {
  const mapContainer: MutableRefObject<HTMLDivElement> = useRef(null);
  const map: MutableRefObject<Map> = useRef(null);
  const user = useLiveQuery(() => globalDB.userData.toArray());
  const { level, scenario, assignments } = useMapContextStore((store) => store);
  const [openDrawer, setOpenDrawer] = useState();
  const [notificationMessage, setNotificationMessage] = useState<string>();
  const levelRef = useRef(level);
  const updateMapMode = useUpdateMapMode();
  const mode = useMapMode();
  if (!mode) updateMapMode("assignments");
  const { width } = useWindowDimensions() || {};
  const [showMultiSchoolModal, setShowMultiSchoolModal] = useState<
    | null
    | {
        sch_level: SchoolType;
        sch_id: SchoolID;
        fac_id: string;
        sch_name: string;
      }[]
  >(null);
  const selectedSchools = useLiveQuery(() => mapDB.selectedSchool.toArray());
  const selectedSchool = useLiveQuery(() =>
    mapDB.selectedSchool.where("active").equals(1).toArray(),
  )?.[0];
  const [mapLoaded, setMapLoaded] = useState(false);
  const router = useRouter();
  const blocks = useBlocks();
  const blocksBySchool = useBlocksBySchoolData();
  const schools = useSchoolData();
  const editingContextStoreRef = useEditingContextStoreRef();
  const enableEditMode = useEnableEditMode();
  const [searchedAddressPoint, setSearchedAddressPoint] =
    useState<AddressPoint>(null);
  const allBoundaries = useCreateBoundaryForAllSchools();
  useEffect(() => {
    if (mapLoaded && allBoundaries)
      // @ts-expect-error - TODO: Figure out why this is not defined
      map.current?.getSource(MAP_LAYERS.allBoundaries)?.setData({
        type: "FeatureCollection",
        features: Object.values(allBoundaries),
      });
  }, [allBoundaries, mapLoaded]);

  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    if (!editingContextStoreRef.current.currentZone) return;
    const swaps = getSwaps(
      {
        esID: getIdFromSchoolPlusLevel(
          editingContextStoreRef.current.currentZone.esID,
        ),
        msID: getIdFromSchoolPlusLevel(
          editingContextStoreRef.current.currentZone.msID,
        ),
        hsID: getIdFromSchoolPlusLevel(
          editingContextStoreRef.current.currentZone.hsID,
        ),
      },
      blocks,
      schools,
    );
    setSwapHighlighting(swaps, map);
  }, [blocks]);

  useEffect(() => {
    if (!map.current || !blocks || !mapLoaded) return;
    if (mode === "edit") {
      return enableEditMode(map, blocks, schools);
    }
    if (mode === "view") {
      return enableViewMode(map, setShowMultiSchoolModal, levelRef);
    }
    if (mode === "assignments") {
      return enableAssignmentMode(
        map,
        setShowMultiSchoolModal,
        levelRef,
        router,
      );
    }
    map.current.getCanvas().style.cursor = "pointer";
  }, [map.current, blocks, mode, mapLoaded]);

  useEffect(() => {
    if (map.current) return;
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style:
        "https://api.maptiler.com/maps/dataviz/style.json?key=KpClencl43lInUzZ6533",
      center: CENTER,
      maxBounds: BOUNDS,
      zoom: 10,
    });

    map.current.on("load", () => {
      map.current.loadImage("/static/house-solid.png", (error, image) => {
        if (error) throw error;
        map.current.addImage("house-marker", image);
      });
      map.current.loadImage("/static/hatch.png", (error, image) => {
        if (error) throw error;
        map.current.addImage("hatch-pattern", image);
      });
      map.current.loadImage("/static/dot.png", (error, image) => {
        if (error) throw error;
        map.current.addImage("dot-pattern", image, { pixelRatio: 25 });
      });
      map.current.loadImage("/static/horizontal3.png", (error, image) => {
        if (error) throw error;
        map.current.addImage("horizontal-pattern", image, { pixelRatio: 1 });
      });
      map.current.addSource(MAP_LAYERS.swappableBlocks, SOURCE_PB_SWAPPABLE);
      map.current.addSource(MAP_LAYERS.schoolPoints, SOURCE_SCHOOL_POINTS);
      map.current.addSource(MAP_LAYERS.planningBlocks, SOURCE_PB);
      map.current.addSource(MAP_LAYERS.planningBlockChanges, SOURCE_PB);
      map.current.addSource(MAP_LAYERS.dualRights, SOURCE_PB);
      map.current.addSource(MAP_LAYERS.allBoundaries, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
        promoteId: "id",
      });
      map.current.addSource(MAP_LAYERS.searchedAddress, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry: searchedAddressPoint,
            },
          ],
        },
      });
      map.current.addSource(
        MAP_LAYERS.schoolBoundaries,
        SOURCE_SCHOOL_BOUNDARIES,
      );
      map.current.addLayer(SCHOOL_POINTS);
      map.current.addLayer(SEARCHED_ADD_LAYER);
      map.current.addLayer(PLANNING_BLOCK_LAYER, MAP_LAYERS.schoolPoints);
      map.current.addLayer(
        PLANNING_BLOCK_CHANGE_LAYER,
        MAP_LAYERS.schoolPoints,
      );
      map.current.addLayer(DUAL_RIGHTS_LAYER, MAP_LAYERS.schoolPoints);
      map.current.addLayer(PLANNING_BLOCK_SWAPPABLE);
      map.current.addLayer(ALL_BOUNDARIES_LAYER);
      map.current.addLayer(SCHOOL_BOUNDARIES);

      setMapLoaded(true);
      map.current.resize();
    });
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !mapLoaded) return;
    colorBlocksByHovered(map, editingContextStoreRef, blocksBySchool);
    showBoundariesByHovered(map, editingContextStoreRef, level);
    increaseOpacityWhenSelected(map, editingContextStoreRef, blocksBySchool);
  }, [
    mapLoaded,
    mode,
    level,
    blocksBySchool,
    editingContextStoreRef?.current?.currentZone,
    editingContextStoreRef?.current?.schoolSelected,
    editingContextStoreRef?.current?.hoveredBlock,
  ]);

  useEffect(() => {
    if (mode !== "assignments" || !mapLoaded) return;
    const school = assignments?.schools?.[`${scenario}`].assignments[level];
    if (school) {
      filterBySchool(map, school, level, scenario);
      highlightAssignmentBlocks(map, level, school, scenario);
      showHatchOnChangedBlocksAssignments(map, scenario, level, school);
      showDualRightsOnChangedBlocksAssignments(map, scenario, level, school);
    } else {
      filterSchoolsByLevel(map, level, scenario);
      removeHighlightOnAssignmentBlocks(map);
      hatchOnBoundaryChanges(map, scenario, level);
      shadeDualRightsChanges(map, scenario, level);
    }
    colorBlocksByLevelAndScenario(map, level, scenario);
  }, [assignments, mapLoaded, level, scenario, mode]);

  useEffect(() => {
    if (selectedSchools?.length > 0 && user?.[0]) {
      const activeSchools = selectedSchools.filter((school) => school.active);
      activeSchools.forEach((school) => {
        axios.post("user_data/", {
          user_id: user[0].user_id,
          type: "school_click",
          user_data: {
            sch_id: school.school_data.sch_id,
            fac_id: school.school_data.fac_id,
          },
        });
      });
    }
  }, [selectedSchools]);

  useEffect(() => {
    // after two seconds, set to false
    setTimeout(() => {
      setNotificationMessage(undefined);
    }, 3000);
  }, [notificationMessage]);

  // TODO: move this logic to the search bar. Watch for assignments and take action in this page.
  useEffect(() => {
    updateSearchedAddressPoint(assignments?.geometry);
    if (assignments?.geometry && mode === "assignments") {
      map.current.flyTo({
        center: [
          assignments.geometry.coordinates[0],
          assignments.geometry.coordinates[1],
        ],
        zoom: 12,
      });
    }
  }, [assignments, mode, mapLoaded]);

  const updateSearchedAddressPoint = (points: AddressPoint) => {
    if (!map.current) return;
    const lyr = map.current.getLayer(MAP_LAYERS.searchedAddress);
    if (!lyr) return;
    (
      map.current.getSource(MAP_LAYERS.searchedAddress) as GeoJSONSource
    ).setData({
      type: "FeatureCollection",
      // @ts-expect-error TODO: Figure out this type issue.
      features: points
        ? [
            {
              type: "Feature",
              properties: {},
              geometry: points,
            },
          ]
        : [],
    });
    setSearchedAddressPoint(points);
  };

  useEffect(() => {
    if (!mapLoaded || mode !== "view") return;
    filterSchoolsByLevel(map, level, scenario);
    colorBlocksByLevelAndScenario(map, level, scenario);
    hatchOnBoundaryChanges(map, scenario, level);
    shadeDualRightsChanges(map, scenario, level);
  }, [level, mapLoaded, mode, scenario]);

  // Visuals in `view` mode which depen on selectedSchool + levels
  useEffect(() => {
    if (!mapLoaded || mode !== "view") return;
    if (selectedSchool && selectedSchool.school_data.sch_level === level) {
      highlightSelectedSchool(
        map,
        selectedSchool.school_data.sch_id,
        level,
        scenario,
      );
    } else {
      resetBlockOpacities(map);
    }
  }, [level, mapLoaded, selectedSchool, mode, scenario]);

  // Visuals in `view` mode which depend only on level.
  useEffect(() => {
    if (!mapLoaded || mode !== "view") return;
    filterSchoolsByLevel(map, level, scenario);
    filterBlocksByLevel(map, level, scenario);
  }, [level, mapLoaded, mode]);

  useEffect(() => {
    if (!mapLoaded || mode !== "edit") return;
    showSchoolsByLevelEditor(map, level);
  }, [level, mapLoaded, mode]);

  useEffect(() => {
    if (!mapLoaded || mode === "edit") return;
    scenarioBoundaryOpacity(map, level, scenario);
  }, [scenario, level, mapLoaded, mode]);

  return (
    <>
      <Box
        sx={{
          overflow: "hidden",
          width: "100vw",
          height: "100svh",
          display: "flex",
          flexDirection: "column",
        }}
        id={"map-outer-box"}
      >
        <MapHeader />
        <Box sx={{ height: "100%" }}>
          <Grid
            container
            spacing={0}
            sx={{
              height: "100%",
            }}
          >
            {width >= 900 ? (
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                lg={4}
                xl={4}
                sx={{
                  zIndex: 2,
                  height: "100%",
                  boxShadow:
                    "4px 0px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    backgroundColor: "white",
                  }}
                >
                  <MapSidebar />
                </Box>
              </Grid>
            ) : null}
            {/* map controls */}
            <Grid
              item
              xs={12}
              sm={12}
              md={8}
              lg={8}
              xl={8}
              sx={{ position: "relative", height: "100%" }}
            >
              <div
                ref={mapContainer}
                className="map"
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <MobileControls
                  openDrawer={openDrawer}
                  setOpenDrawer={setOpenDrawer}
                />
                <Selectors />
                <MapLegend />
              </div>
            </Grid>
          </Grid>
          <NotificationSnackBar message={notificationMessage} />
        </Box>
      </Box>

      {width <= 900 && mode === "edit" ? <NoEditModal /> : null}
      <MultiSelectModal
        showMultiSchoolModal={showMultiSchoolModal}
        setShowMultiSchoolModal={setShowMultiSchoolModal}
      />
    </>
  );
};
